import { type TextFieldProps } from '@mui/material'
import type { ReactNode } from 'react'
import { useCallback } from 'react'

import type { Localizable } from '@salsita-ai/platform-common/utils'
import type { DomainEntityId } from '@salsita-ai/platform-project-sdk/domain'
import {
  DomainControlsAnalytics,
  useGenericDimension,
  useResolveLocalizable,
} from '@salsita-ai/platform-project-sdk/react'
import { NumericalInput } from '@salsita-ai/platform-project-sdk/ui'

export interface GenericDimensionInputProps {
  /**
   * DomainEntityId to be updated.
   */
  entityId: DomainEntityId<string>
  /**
   * The name of the instance to be used for the dimension update.
   */
  instanceName: string
  /**
   * An array of fixable violations. They will be ignored in the violation hint.
   */
  fixableViolations?: string[]
  label?: Localizable
  helperText?: Localizable
  startAdornment?: ReactNode
  size?: TextFieldProps['size']
  variant?: TextFieldProps['variant']
}

export const CustomGenericDimensionInput = (props: GenericDimensionInputProps) => {
  const { entityId, instanceName, helperText, startAdornment, size, fixableViolations = [], label } = props

  const resolveLocalizable = useResolveLocalizable()
  const { min, max, value, unit, hints, hasErrors, update, resolve } = useGenericDimension(
    entityId,
    instanceName,
    fixableViolations,
    DomainControlsAnalytics.GenericDimensionInputChanged
  )

  const updateAndResolve = useCallback(() => {
    update(value)
    resolve()
  }, [update, resolve, value])

  return (
    <NumericalInput
      value={value}
      min={min ?? 0}
      max={max ?? 700}
      onChange={update}
      onChanged={updateAndResolve}
      unit={unit}
      hasError={hasErrors}
      hintMessage={hints}
      helperText={resolveLocalizable(helperText)}
      size={size}
      inputFieldProps={{
        label: resolveLocalizable(label),
        startAdornment,
        invalidNumberErrorMessage: 'Please provide a valid number!',
      }}
    />
  )
}
